import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import {
  ChevronDown,
  ChevronRight,
  Community,
  CubeOutline,
  Icon,
  Image2,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func } from 'prop-types'

import {
  importCommunityGalleryPanel,
} from 'Pods/ThreeDStudio/Components/LazyCommunityGalleryPanel/LazyCommunityGalleryPanel'
import { STUDIO_PATH, iconSize, iconSizeNav } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import {
  readStoredCommunityNavExpanded,
  saveCommunityNavExpanded,
} from 'Pods/ThreeDStudio/Services/communityNavStorage'

import useFocusTrap from 'Utils/useFocusTrap'

import SubNavItem from './Components/SubNavItem/SubNavItem'

// Gap between the collapsed rail button and the flyout it opens.
const FLYOUT_GAP = 4

const CHILDREN_ID = 'link-nav-community-children'
const FLYOUT_ID = 'link-nav-community-flyout'
const FLYOUT_TITLE_ID = 'link-nav-community-flyout-title'

function CommunityNav({
  isOpen = true,
  isThreeDModelsActive = false,
  isImagesActive = false,
  onNavigate,
}) {
  const t = useTranslation()
  // Expanded at SSR so the server-rendered nav already occupies its final height; a
  // stored collapse is applied after hydration and only shifts the nav for the viewer
  // who chose it.
  const [isExpanded, setIsExpanded] = useState(true)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [flyoutPosition, setFlyoutPosition] = useState(null)
  const triggerRef = useRef(null)
  const { trapRef } = useFocusTrap(isFlyoutOpen)
  const isSectionActive = isThreeDModelsActive || isImagesActive

  useEffect(() => {
    if (readStoredCommunityNavExpanded() === false) setIsExpanded(false)
  }, [])

  useEffect(() => {
    if (!isFlyoutOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsFlyoutOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFlyoutOpen])

  const handleToggle = useCallback(() => {
    setIsExpanded((wasExpanded) => {
      saveCommunityNavExpanded(!wasExpanded)
      return !wasExpanded
    })
  }, [])

  // The flyout is fixed rather than absolute because `.sidebar` clips its overflow,
  // so it is measured off the rail button each time it opens.
  const handleFlyoutToggle = useCallback(() => {
    if (!isFlyoutOpen && triggerRef.current) {
      const { top, right } = triggerRef.current.getBoundingClientRect()
      setFlyoutPosition({ top, left: right + FLYOUT_GAP })
    }

    setIsFlyoutOpen(!isFlyoutOpen)
  }, [isFlyoutOpen])

  const handleFlyoutNavigate = useCallback(() => {
    setIsFlyoutOpen(false)
    onNavigate()
  }, [onNavigate])

  // The rail line belongs to the indented list in the expanded sidebar; inside the
  // collapsed rail's flyout the same rows render as a plain menu.
  const children = (
    <>
      <SubNavItem
        to={STUDIO_PATH.COMMUNITY_THREE_D_MODELS}
        label={t('sidebar.linkNav.communityThreeDModels')}
        icon={CubeOutline}
        isActive={isThreeDModelsActive}
        withRail={isOpen}
        onClick={isOpen ? onNavigate : handleFlyoutNavigate}
        onMouseEnter={importCommunityGalleryPanel}
        onFocus={importCommunityGalleryPanel}
      />
      <SubNavItem
        to={STUDIO_PATH.COMMUNITY_IMAGES}
        label={t('sidebar.linkNav.communityImages')}
        icon={Image2}
        isActive={isImagesActive}
        withRail={isOpen}
        onClick={isOpen ? onNavigate : handleFlyoutNavigate}
      />
    </>
  )

  if (!isOpen) {
    return (
      <div className="community-nav community-nav--collapsed">
        <button
          ref={triggerRef}
          type="button"
          className={classnames(
            'link-nav__item link-nav__item--toggle',
            (isFlyoutOpen || isSectionActive) && 'link-nav__item--active',
          )}
          aria-label={t('sidebar.linkNav.community')}
          aria-expanded={isFlyoutOpen}
          aria-controls={FLYOUT_ID}
          onClick={handleFlyoutToggle}
        >
          <Icon
            svgComponent={Community}
            size={iconSizeNav}
            color={isFlyoutOpen || isSectionActive ? 'primary' : 'darker'}
            notSpaced
          />
        </button>
        {isFlyoutOpen && (
          <>
            <div
              className="community-nav__scrim"
              aria-hidden="true"
              onClick={handleFlyoutToggle}
            />
            <div
              ref={trapRef}
              id={FLYOUT_ID}
              className="community-nav__flyout"
              style={flyoutPosition}
              role="group"
              aria-labelledby={FLYOUT_TITLE_ID}
            >
              <span id={FLYOUT_TITLE_ID} className="community-nav__flyout-title cgt-overline-sm">
                {t('sidebar.linkNav.community')}
              </span>
              {children}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="community-nav">
      <button
        type="button"
        className={classnames(
          'link-nav__item link-nav__item--toggle cgt-body-base-semibold',
          isSectionActive && 'link-nav__item--section-active',
        )}
        aria-expanded={isExpanded}
        aria-controls={CHILDREN_ID}
        onClick={handleToggle}
      >
        <Icon
          svgComponent={Community}
          size={iconSizeNav}
          color={isSectionActive ? 'primary' : 'darker'}
          notSpaced
        />
        <span className="community-nav__label">{t('sidebar.linkNav.community')}</span>
        <Icon
          svgComponent={isExpanded ? ChevronDown : ChevronRight}
          size={iconSize}
          color={isSectionActive ? 'primary' : 'darker'}
          notSpaced
        />
      </button>
      {isExpanded && (
        <div id={CHILDREN_ID} className="community-nav__children">
          {children}
        </div>
      )}
    </div>
  )
}

CommunityNav.propTypes = {
  isOpen: bool,
  isThreeDModelsActive: bool,
  isImagesActive: bool,
  onNavigate: func.isRequired,
}

export default memo(CommunityNav)
